import { filter, map, difference, flatMap, each, isEqual, last, add } from "../../../utils/lodash";
import { BlockModel } from "../../../models/BlockModel";
import { QuestionModel } from "../../../models/QuestionModel";
import { PageModel } from "../../../models/PageModel";
import { NavigationState } from "../../../models/states/NavigationState";
import { InterviewState } from "../../../models/states/InterviewState";
import {
    getAllCurrentPageQuestions,
    getMaxLoopSize,
    isAutoPageBreak,
    maxNumberOfQuestionsPerPage,
} from "../../../models/utilities/blockUtilities";
import { Log } from "../../../utils/logging";
import {
    getQuestionWithName,
    getBlock,
    getBlockWithVariable,
} from "../../../models/utilities/questionnaireUtilities";
import { getPageIdentifier } from "../../../models/utilities/pageUtilities";
import { getSettingString } from "../../../models/utilities/settingUtilities";
import { QuestionnaireModel } from "../../../models/QuestionnaireModel";
import { isNullOrEmpty } from "../../../utils";
import { getActiveQuesions } from "../../../models/utilities/questionUtilities";
import { NavigationResultPageModel } from "../../../models/NavigationResultPageModel";

const TAG = "navigationUtilities";

export const getCurrentBlock = (state: NavigationState) => {
    if (state.randomizedBlocks && state.randomizedBlocks.length > 0) {
        return state.randomizedBlocks[state.blockIndex];
    }
    return undefined;
};

export const blockWillChange = (state: NavigationState, newState: NavigationState) =>
    state.questionIndex < 0 || state.blockIndex !== newState.blockIndex;

export const getCurrentQuestion = (state: NavigationState) => {
    const block = getCurrentBlock(state);
    if (block === undefined || block.questions.length === 0) {
        return undefined;
    }
    return block.questions[state.questionIndex];
};

export const getBlockCurrentLoop = (blockName: string, state: NavigationState) =>
    state.loopByBlockName[blockName.toUpperCase()] || 1;

export const getCurrentLoop = (state: NavigationState) => {
    const block = getCurrentBlock(state);
    return getBlockCurrentLoop(block.name, state);
};

export const hasNextBlock = (state: NavigationState) =>
    state.randomizedBlocks && state.randomizedBlocks.length > state.blockIndex + 1;

export const hasNextLoop = (state: NavigationState) => {
    const block = getCurrentBlock(state);
    return block && getBlockCurrentLoop(block.name, state) < getMaxLoopSize(block);
};

export const hasNextQuestion = (state: NavigationState) => {
    const block = getCurrentBlock(state);
    return block && block.questions.length - 1 > state.questionIndex;
};

export const hasPreviousPage = (state: NavigationState) => {
    if (state.currentPageOnServer) {
        return !state.currentPageOnServer.isFirstPage;
    }
    return state.history.length > 0;
};

export const hasNextPage = (state: NavigationState) => {
    if (state.currentPageOnServer) {
        return !state.currentPageOnServer.isLastPage;
    }
    return (
        state.exitTo !== undefined ||
        hasNextQuestion(state) ||
        hasNextLoop(state) ||
        hasNextBlock(state)
    );
};

export const getVariableRow = (
    state: NavigationState,
    questionnaire: QuestionnaireModel,
    variable: string,
) => {
    const block = getBlockWithVariable(questionnaire, variable);
    if (block == null || state.loopByBlockName == null) return 1;
    return state.loopByBlockName[block.name.toUpperCase()] || 1;
};

export const nextQuestion = (state: NavigationState) => ({
    ...state,
    questionIndex: state.questionIndex + 1,
});

export const nextLoop = (state: NavigationState) => ({
    ...state,
    questionIndex: 0,
    loopByBlockName: {
        ...state.loopByBlockName,
        [getCurrentBlock(state).name.toUpperCase()]: getCurrentLoop(state) + 1,
    },
});

export const nextBlock = (state: NavigationState) => {
    const block = state.randomizedBlocks[state.blockIndex + 1];
    return {
        ...state,
        questionIndex: 0,
        blockIndex: state.blockIndex + 1,
        loopByBlockName: {
            ...state.loopByBlockName,
            [block.name.toUpperCase()]: 1,
        },
    };
};

export const next = (state: NavigationState) => {
    if (!hasNextPage(state)) {
        return state;
    }
    if (hasNextQuestion(state)) {
        return nextQuestion(state);
    }
    if (hasNextLoop(state)) {
        return nextLoop(state);
    }
    if (hasNextBlock(state)) {
        return nextBlock(state);
    }
    return state;
};

export function getRandomizedQuestionWithName(state: NavigationState, name: string) {
    if (isNullOrEmpty(name)) return undefined;
    const ids = state.randomizedQuestionIdByName[name.toUpperCase()];
    if (ids == null) return undefined;
    const block = state.randomizedBlocks[ids[0]];
    if (block == null) return undefined;
    return block.questions[ids[1]];
}

export const branchToAbsolute = (
    state: NavigationState,
    questionnaire: QuestionnaireModel,
    questionName: string,
    loopIndex?: number,
) => {
    const dest = getQuestionWithName(questionnaire, questionName);
    if (dest === undefined) {
        Log.e(TAG, "BRANCHING_QUESTION_NOT_FOUND", questionName);
        return state;
    }

    const [blockIndex, questionIndex] = state.randomizedQuestionIdByName[questionName];
    const block = state.randomizedBlocks[blockIndex];
    if (!loopIndex) {
        loopIndex = blockIndex === state.currentPage.blockIndex ? state.currentPage.loopIndex : 1;
    }

    const newState = {
        ...state,
        blockIndex,
        questionIndex,
        loopByBlockName: {
            ...state.loopByBlockName,
            [block.name.toUpperCase()]: loopIndex,
        },
    };
    return newState;
};

export const branchToRelative = (
    state: NavigationState,
    questionnaire: QuestionnaireModel,
    originQuestionName: string,
    count: number,
) => {
    state = branchToAbsolute(state, questionnaire, originQuestionName, getCurrentLoop(state));
    for (let i = 0; i < count; i++) {
        state = next(state);
    }
    return state;
};

export const branchToBlock = (
    state: NavigationState,
    questionnaire: QuestionnaireModel,
    blockName: string,
) => {
    const dest = getBlock(questionnaire, blockName);
    if (dest === undefined) {
        Log.e(TAG, "BRANCHING_BLOCK_NOT_FOUND", blockName);
        return undefined;
    }
    return {
        ...state,
        questionIndex: 0,
        blockIndex: dest.index,
    };
};

export const updateNavigationButtons = (state: NavigationState): NavigationState => ({
    ...state,
    hasNextPage: hasNextPage(state),
    hasPreviousPage: hasPreviousPage(state),
});

export const getProgressBarStartQuestion = (
    state: NavigationState,
    questionnaire: QuestionnaireModel,
) =>
    getRandomizedQuestionWithName(
        state,
        getSettingString(questionnaire, "PROGRESSBARSTARTQUESTION", ""),
    );

export const getProgressBarEndQuestion = (
    state: NavigationState,
    questionnaire: QuestionnaireModel,
) =>
    getRandomizedQuestionWithName(
        state,
        getSettingString(questionnaire, "PROGRESSBARENDQUESTION", ""),
    );

const getProgressBarBlocks = (
    blocks: BlockModel[],
    state: NavigationState,
    questionnaire: QuestionnaireModel,
) => {
    let blocksWithProgress = blocks;
    const startQuestion = getProgressBarStartQuestion(state, questionnaire);
    const endQuestion = getProgressBarEndQuestion(state, questionnaire);

    // Filter blocks according to the start and end questions
    if (startQuestion) {
        blocksWithProgress = filter(blocksWithProgress, (b) => b.index >= startQuestion.blockIndex);
    }
    if (endQuestion) {
        blocksWithProgress = filter(blocksWithProgress, (b) => b.index <= endQuestion.blockIndex);
    }

    return map(blocksWithProgress, (b) => {
        let questions = getActiveQuesions(b.questions);

        // Filter questions according to start and end questions
        if (startQuestion && b.index === startQuestion.blockIndex) {
            questions = filter(questions, (q) => q.index >= startQuestion.index);
        }
        if (endQuestion && b.index === endQuestion.blockIndex) {
            questions = filter(questions, (q) => q.index <= endQuestion.index);
        }
        return {
            ...b,
            questions,
        };
    });
};

export const getProgress = (
    state: NavigationState,
    page: PageModel,
    questionnaire: QuestionnaireModel,
    blocks: BlockModel[],
) => {
    const blocksWithProgress = getProgressBarBlocks(blocks, state, questionnaire);
    const pageQuestions = getActiveQuesions(page.questions);
    // Check whether some of the current page questions has progress bar
    if (
        difference(
            pageQuestions,
            flatMap(blocksWithProgress, (b) => b.questions),
        ).length === pageQuestions.length
    ) {
        return -1;
    }
    let progress = 0;
    each(blocksWithProgress, (block) => {
        if (page.blockIndex === block.index) {
            progress += block.questions.length * (page.loopIndex - 1);

            // Adjust question index to consider filtered questions
            each(block.questions, (q, i) => {
                if (isEqual(q, last(pageQuestions))) {
                    progress += i + 1;
                }
            });
            return false;
        }
        progress += block.questions.length * getMaxLoopSize(block);
        return undefined;
    });
    const totalQuestions = blocksWithProgress
        .map((b) => b.questions.length * getMaxLoopSize(b))
        .reduce(add);
    return Math.trunc((progress / totalQuestions) * 100);
};

export const logPageSeparator = (page: PageModel, block: BlockModel) => {
    const pageIdentifier = getPageIdentifier(page, block);
    const paddingSize = Math.max(0, (50 - pageIdentifier.length) / 2);
    const prefix = "-".repeat(Math.floor(paddingSize));
    const suffix = "-".repeat(Math.ceil(paddingSize));
    Log.v(TAG, prefix + pageIdentifier + suffix);
};

export const updateHistory = (state: NavigationState) => {
    let { history } = state;
    if (state.currentPage !== undefined) history = history.concat(state.currentPage);
    return {
        ...state,
        history,
    };
};

export const setPage = (state: NavigationState, page: PageModel, isPrevious?: boolean) => {
    if (!isPrevious && state.currentPage !== undefined && state.currentPage.isReady)
        state = updateHistory(state);
    const block = state.randomizedBlocks[page.blockIndex];
    state = {
        ...state,
        exitTo: undefined,
        questionIndex: page.questions.slice(-1)[0].index,
        loopByBlockName: {
            ...state.loopByBlockName,
            [block.name.toUpperCase()]: page.loopIndex,
        },
        blockIndex: page.blockIndex,
        currentPage: { ...page, isPrevious, isReady: false },
        history: isPrevious ? state.history.slice(0, -1) : state.history,
        message: undefined,
    };
    logPageSeparator(page, block);
    return updateNavigationButtons(state);
};

export const buildPageFromServer = (
    state: NavigationState,
    interview: InterviewState,
    serverPage: NavigationResultPageModel,
): NavigationState => {
    const page: PageModel = {
        blockIndex: getBlockIndexById(serverPage.blockId, interview.questionnaire?.blocks),
        loopIndex: serverPage.matrix,
        questions: getServerCurrentQuestions(serverPage, interview.questionnaire),
        progress: serverPage.progress,
        isPrevious: null,
        isReady: false,
        isShortcut: null,
    };
    const newState = {
        ...state,
        randomizedBlocks: interview.questionnaire?.blocks,
        currentPageOnServer: serverPage,
    };
    return setPage(newState, page);
};

const getBlockIndexById = (blockId: number, randomizedBlocks: BlockModel[]) =>
    randomizedBlocks.find((b) => b.id === blockId)?.index;

export const getServerCurrentQuestions = (
    page: NavigationResultPageModel,
    questionnaire: QuestionnaireModel,
) => page.questions.map((q) => getQuestionWithName(questionnaire, q.name));

export const buildPage = (
    state: NavigationState,
    interview: InterviewState,
    exitTo?: PageModel,
): any => {
    const block = getCurrentBlock(state);

    // Generate the list of questions.
    const questions: QuestionModel[] = [];
    while (true) {
        const question = getCurrentQuestion(state);
        if (!question) {
            break;
        }
        questions.push(question);
        if (
            !hasNextQuestion(state) ||
            question.isPageBreak ||
            (isAutoPageBreak(block) && questions.length >= maxNumberOfQuestionsPerPage(block))
        ) {
            break;
        }
        state = nextQuestion(state);
    }

    // Generate the page.
    const page: PageModel = {
        blockIndex: state.blockIndex,
        loopIndex: getCurrentLoop(state),
        questions,
        progress: exitTo != null ? state.currentPage.progress : 0,
        isPrevious: false,
        isReady: false,
        isShortcut: exitTo != null,
    };

    // Calculate progress if this is not a shortcut.
    if (!page.isShortcut)
        page.progress = getProgress(state, page, interview.questionnaire, state.randomizedBlocks);

    // Set current page and add to history.
    return {
        ...setPage(state, page),
        exitTo,
    };
};

export const previous = (state: NavigationState) => {
    if (!hasPreviousPage(state)) return state;
    if (state.exitTo == null) state.history = state.history.filter((h) => !h.isShortcut);
    const previousPage = state.history.slice(-1)[0];
    return setPage(state, previousPage, true);
};

export const pageReady = (state: NavigationState) => ({
    ...state,
    currentPage: {
        ...state.currentPage,
        isReady: true,
    },
});

export const currentPageVariableDependencies = (state: NavigationState) =>
    getAllCurrentPageQuestions(getCurrentBlock(state), state.questionIndex).flatMap(
        (q) => q.variableDependencies,
    );
